import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 8; // ~16 seconds worst case
const POLL_INTERVAL_MS = 2000;

/**
 * Pagina intermedia post-redirect da Stripe.
 * Fa polling su /api/payments/status/:session_id.
 * Se `paid` → prepara l'oggetto ordine e naviga a /checkout/conferma.
 * Se `expired/failed` → mostra errore + link a /carrello.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { items, subtotal, totalQuantity, clearCart } = useCart();
  const sessionId = params.get("session_id");
  const [error, setError] = useState("");
  const pollsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError("Sessione di pagamento non valida.");
      return;
    }

    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const { data } = await axios.get(
          `${API}/api/payments/status/${sessionId}`
        );
        if (data.payment_status === "paid") {
          stoppedRef.current = true;
          // Costruisci l'oggetto ordine finale (per la pagina di conferma).
          let customer = {};
          try {
            customer = JSON.parse(
              window.sessionStorage.getItem("time-inside:pending-customer") || "{}"
            );
          } catch {
            /* ignore */
          }
          const order = {
            id: `ORD-${sessionId.slice(-10).toUpperCase()}`,
            sessionId,
            createdAt: new Date().toISOString(),
            customer,
            paymentMethod: "card",
            items: items.map((it) => ({
              slug: it.slug,
              title: it.title,
              subtitle: it.subtitle,
              format: it.format,
              price: it.price,
              quantity: it.quantity,
            })),
            subtotal,
            total: (data.amount || 0) / 100 || subtotal,
            totalQuantity,
            currency: data.currency || "eur",
            status: "pagato",
          };
          try {
            window.sessionStorage.setItem(
              "time-inside:last-order",
              JSON.stringify(order)
            );
            window.sessionStorage.removeItem("time-inside:pending-customer");
          } catch {
            /* ignore */
          }
          navigate("/checkout/conferma", { replace: true });
          clearCart();
          return;
        }
        if (
          data.payment_status === "failed" ||
          data.payment_status === "expired" ||
          data.status === "failed" ||
          data.status === "expired"
        ) {
          stoppedRef.current = true;
          setError(
            "Il pagamento non è andato a buon fine. Il tuo carrello è ancora disponibile."
          );
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          stoppedRef.current = true;
          setError(
            "Stiamo ancora verificando il pagamento. Controlla l'email o riprova tra qualche istante."
          );
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        stoppedRef.current = true;
        setError(
          err?.response?.data?.detail ||
            "Impossibile verificare lo stato del pagamento."
        );
      }
    };

    poll();
    return () => {
      stoppedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (error) {
    return (
      <div
        data-testid="payment-error"
        className="px-5 sm:px-8 lg:px-12 py-24 max-w-2xl mx-auto text-center"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 border border-red-400/30 flex items-center justify-center mb-6">
          <XCircle className="w-7 h-7 text-red-300" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-extralight tracking-tight text-white">
          Pagamento non completato
        </h1>
        <p className="mt-4 text-white/60 font-light">{error}</p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/carrello"
            data-testid="payment-back-cart"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors text-[11px] tracking-[0.28em] uppercase font-mono"
          >
            Torna al carrello
          </Link>
          <Link
            to="/checkout"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 transition-colors text-[11px] tracking-[0.28em] uppercase font-medium"
          >
            Riprova
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="payment-verifying"
      className="px-5 sm:px-8 lg:px-12 py-28 max-w-2xl mx-auto text-center"
    >
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center mb-6 shadow-[0_10px_40px_-10px_rgba(16,185,129,0.5)]">
        <Loader2 className="w-7 h-7 text-emerald-300 animate-spin" />
      </div>
      <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/80 mb-3">
        Verifica pagamento
      </div>
      <h1 className="text-3xl sm:text-4xl font-extralight tracking-tight text-white">
        Attendere qualche istante…
      </h1>
      <p className="mt-4 text-white/60 font-light">
        Stiamo confermando il pagamento con Stripe. Non chiudere questa pagina.
      </p>
    </div>
  );
}
